/*
 * TFX Banner Footer - slider behavior (dots + swipe + mouse drag)
 * Objective: stable column widths, predictable masking on resize, and clean Theme Editor re-init.
 * Dependencies: none (vanilla JS)
 * Date: 2026-03-05
 * Rollback: remove this file and the script tag in sections/tfx-banner-footer.liquid.
 */
(function () {
  if (window.TFXBannerFooterPager) return;

  var instances = new WeakMap();

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function findSection(node) {
    if (!node || !(node instanceof Element)) return null;
    if (node.matches('.section-tfx-banner-footer')) return node;
    return node.closest('.section-tfx-banner-footer');
  }

  function getInstance(section) {
    if (!section) return null;
    return instances.get(section) || null;
  }

  function getColumnCount() {
    if (!window.matchMedia) return 4;
    if (window.matchMedia('(max-width: 767.98px)').matches) return 2;
    if (window.matchMedia('(max-width: 1149.98px)').matches) return 3;
    return 4;
  }

  function isInteractiveTarget(target) {
    if (!target || !(target instanceof Element)) return false;
    return Boolean(target.closest('a, button, input, textarea, select, label, summary, [role="button"]'));
  }

  function initSection(section) {
    if (!section || instances.has(section)) return;

    var track = section.querySelector('[data-tfx-banner-footer-track]');
    var dotsRoot = section.querySelector('[data-tfx-banner-footer-dots]');
    if (!track || !dotsRoot) return;

    var dots = [];
    var currentIndex = 0;
    var pageTargets = [0];
    var pageCount = 1;
    var scrollRafId = 0;
    var rebuildRafId = 0;
    var resizeObserver = null;
    var fixedItemWidth = 0;
    var mdMediaQuery = window.theme && theme.mediaQueries && theme.mediaQueries.md
      ? theme.mediaQueries.md
      : '(min-width: 768px)';
    var prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var isPointerDown = false;
    var isDraggingMouse = false;
    var dragPointerId = null;
    var dragStartX = 0;
    var dragStartLeft = 0;
    var dragThreshold = 6;

    function getMaxDotsLimit() {
      var raw = Number(dotsRoot.getAttribute('data-tfx-max-dots') || '3');
      if (!Number.isFinite(raw)) raw = 3;
      var safe = clamp(Math.round(raw), 2, 3);
      dotsRoot.setAttribute('data-tfx-max-dots', String(safe));
      return safe;
    }

    function getBaseItemWidth() {
      var viewport = track.clientWidth;
      if (!viewport) return 0;

      var columns = getColumnCount();
      var trackStyles = window.getComputedStyle(track);
      var gap = parseFloat(trackStyles.columnGap || trackStyles.gap || '32');
      if (!Number.isFinite(gap)) gap = 32;
      var totalGap = (columns - 1) * gap;
      return Math.max(0, Math.floor((viewport - totalGap) / columns));
    }

    function updateUniformItemWidth() {
      var items = track.querySelectorAll('.tfx-banner-footer__item');
      var maxHeadWidth = 0;
      var isMobile = window.matchMedia && !window.matchMedia(mdMediaQuery).matches;
      var previousItemWidth = track.style.getPropertyValue('--tfx-banner-footer-item-width');

      // Always measure from the native CSS width first so Theme Editor resizes
      // can shrink the columns back down instead of locking in a wider value.
      track.style.removeProperty('--tfx-banner-footer-item-width');

      for (var i = 0; i < items.length; i += 1) {
        var head = items[i].querySelector('.tfx-banner-footer__head');
        if (!head) continue;
        var measured = Math.ceil(head.scrollWidth);
        if (measured > maxHeadWidth) {
          maxHeadWidth = measured;
        }
      }

      // Allow width to both grow and shrink when content/editor settings change.
      var baseItemWidth = getBaseItemWidth();
      var nextWidth = isMobile ? baseItemWidth : Math.max(maxHeadWidth, baseItemWidth);
      if (nextWidth > 0 && nextWidth !== fixedItemWidth) {
        fixedItemWidth = nextWidth;
        track.style.setProperty('--tfx-banner-footer-item-width', nextWidth + 'px');
      } else if (nextWidth <= 0) {
        fixedItemWidth = 0;
        track.style.removeProperty('--tfx-banner-footer-item-width');
      } else if (previousItemWidth) {
        track.style.setProperty('--tfx-banner-footer-item-width', previousItemWidth);
      }
    }

    function calculateTargets() {
      var viewport = track.clientWidth;
      if (!viewport) {
        track.classList.remove('is-scrollable');
        return [0];
      }

      var items = track.querySelectorAll('.tfx-banner-footer__item');
      if (!items.length) {
        track.classList.remove('is-scrollable');
        return [0];
      }

      var maxScroll = Math.max(0, track.scrollWidth - viewport);
      var isScrollable = maxScroll > 1;
      track.classList.toggle('is-scrollable', isScrollable);

      if (!isScrollable) {
        return [0];
      }

      var targets = [0];
      var threshold = 2;

      for (var i = 0; i < items.length; i += 1) {
        var left = clamp(items[i].offsetLeft, 0, maxScroll);
        if (Math.abs(left - targets[targets.length - 1]) > threshold) {
          targets.push(left);
        }
      }

      if (Math.abs(maxScroll - targets[targets.length - 1]) > threshold) {
        targets.push(maxScroll);
      }

      return targets;
    }

    function limitTargets(targets, limit) {
      if (!targets || !targets.length) return [0];
      if (targets.length <= limit) return targets.slice();

      var reduced = [];
      for (var i = 0; i < limit; i += 1) {
        var sourceIndex = Math.round((i * (targets.length - 1)) / (limit - 1));
        var value = targets[sourceIndex];
        if (!reduced.length || Math.abs(value - reduced[reduced.length - 1]) > 1) {
          reduced.push(value);
        }
      }

      if (reduced.length === 1 && targets.length > 1) {
        reduced.push(targets[targets.length - 1]);
      }

      if (!reduced.length) {
        return [0];
      }

      return reduced;
    }

    function getClosestPageIndex(scrollLeft) {
      if (!pageTargets.length) return 0;

      var closest = 0;
      var minDiff = Number.POSITIVE_INFINITY;

      for (var i = 0; i < pageTargets.length; i += 1) {
        var diff = Math.abs(scrollLeft - pageTargets[i]);
        if (diff < minDiff) {
          minDiff = diff;
          closest = i;
        }
      }

      return closest;
    }

    function setActive(index) {
      for (var i = 0; i < dots.length; i += 1) {
        var active = i === index;
        dots[i].classList.toggle('is-active', active);
        dots[i].setAttribute('aria-current', active ? 'true' : 'false');
      }
    }

    function goTo(index, smooth) {
      if (!pageTargets.length) return;

      currentIndex = clamp(index, 0, pageTargets.length - 1);
      var target = pageTargets[currentIndex] || 0;

      try {
        track.scrollTo({
          left: target,
          behavior: smooth && !prefersReducedMotion ? 'smooth' : 'auto'
        });
      } catch (error) {
        track.scrollLeft = target;
      }

      setActive(currentIndex);
    }

    function onScroll() {
      if (scrollRafId) return;
      scrollRafId = window.requestAnimationFrame(function () {
        scrollRafId = 0;
        currentIndex = clamp(getClosestPageIndex(track.scrollLeft), 0, Math.max(0, pageCount - 1));
        setActive(currentIndex);
      });
    }

    function cancelMouseDrag() {
      isPointerDown = false;
      isDraggingMouse = false;
      dragPointerId = null;
      track.classList.remove('is-dragging');
    }

    function rebuild() {
      updateUniformItemWidth();

      var maxDots = getMaxDotsLimit();
      pageTargets = limitTargets(calculateTargets(), maxDots);
      pageCount = pageTargets.length;

      dotsRoot.innerHTML = '';
      dots = [];

      var showDots = pageCount > 1;
      dotsRoot.hidden = !showDots;

      if (showDots) {
        for (var i = 0; i < pageCount; i += 1) {
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'tfx-banner-footer__dot';
          btn.setAttribute('aria-label', 'Aller a la page ' + (i + 1));
          btn.dataset.tfxPage = String(i);
          btn.addEventListener('click', function (event) {
            var page = Number(event.currentTarget.dataset.tfxPage || '0');
            goTo(page, true);
          });
          dotsRoot.appendChild(btn);
          dots.push(btn);
        }
      }

      if (pageCount <= 1) {
        currentIndex = 0;
        cancelMouseDrag();
        if (track.scrollLeft !== 0) {
          track.scrollLeft = 0;
        }
      }

      goTo(Math.min(currentIndex, Math.max(0, pageCount - 1)), false);
    }

    function scheduleRebuild() {
      if (rebuildRafId) return;
      rebuildRafId = window.requestAnimationFrame(function () {
        rebuildRafId = 0;
        rebuild();
      });
    }

    function onPointerDown(event) {
      if (event.button !== 0) return;
      if (event.pointerType && event.pointerType !== 'mouse') return;
      if (!track.classList.contains('is-scrollable')) return;
      if (pageCount <= 1) return;
      if (isInteractiveTarget(event.target)) return;

      isPointerDown = true;
      isDraggingMouse = false;
      dragPointerId = event.pointerId;
      dragStartX = event.clientX;
      dragStartLeft = track.scrollLeft;
    }

    function onPointerMove(event) {
      if (!isPointerDown) return;
      if (dragPointerId !== null && event.pointerId !== dragPointerId) return;

      var deltaX = event.clientX - dragStartX;
      if (!isDraggingMouse) {
        if (Math.abs(deltaX) < dragThreshold) return;
        isDraggingMouse = true;
        track.classList.add('is-dragging');

        if (track.setPointerCapture) {
          try {
            track.setPointerCapture(event.pointerId);
          } catch (error) {
            // Ignore capture errors on unsupported browsers.
          }
        }
      }

      event.preventDefault();
      var maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      track.scrollLeft = clamp(dragStartLeft - deltaX, 0, maxScroll);
      onScroll();
    }

    function onPointerUp(event) {
      if (!isPointerDown) return;
      if (dragPointerId !== null && event.pointerId !== dragPointerId) return;

      if (track.releasePointerCapture) {
        try {
          track.releasePointerCapture(event.pointerId);
        } catch (error) {
          // Ignore release errors if capture is already gone.
        }
      }

      cancelMouseDrag();
    }

    function onDragStart(event) {
      event.preventDefault();
    }

    function onWindowBlur() {
      cancelMouseDrag();
    }

    function scrollToItem(itemElement) {
      if (!itemElement || !pageTargets.length) return;
      var itemLeft = itemElement.offsetLeft;
      var targetIndex = getClosestPageIndex(itemLeft);
      goTo(targetIndex, false);
    }

    track.addEventListener('scroll', onScroll, { passive: true });
    track.addEventListener('pointerdown', onPointerDown);
    track.addEventListener('pointermove', onPointerMove);
    track.addEventListener('pointerup', onPointerUp);
    track.addEventListener('pointercancel', onPointerUp);
    track.addEventListener('pointerleave', onPointerUp);
    track.addEventListener('dragstart', onDragStart);

    window.addEventListener('resize', scheduleRebuild);
    window.addEventListener('orientationchange', scheduleRebuild);
    window.addEventListener('load', scheduleRebuild);
    window.addEventListener('blur', onWindowBlur);

    if (window.ResizeObserver) {
      resizeObserver = new window.ResizeObserver(function () {
        scheduleRebuild();
      });
      resizeObserver.observe(section);
      resizeObserver.observe(track);
    }

    scheduleRebuild();

    instances.set(section, {
      scrollToItem: scrollToItem,
      destroy: function () {
        if (scrollRafId) {
          window.cancelAnimationFrame(scrollRafId);
          scrollRafId = 0;
        }
        if (rebuildRafId) {
          window.cancelAnimationFrame(rebuildRafId);
          rebuildRafId = 0;
        }

        track.removeEventListener('scroll', onScroll);
        track.removeEventListener('pointerdown', onPointerDown);
        track.removeEventListener('pointermove', onPointerMove);
        track.removeEventListener('pointerup', onPointerUp);
        track.removeEventListener('pointercancel', onPointerUp);
        track.removeEventListener('pointerleave', onPointerUp);
        track.removeEventListener('dragstart', onDragStart);

        window.removeEventListener('resize', scheduleRebuild);
        window.removeEventListener('orientationchange', scheduleRebuild);
        window.removeEventListener('load', scheduleRebuild);
        window.removeEventListener('blur', onWindowBlur);

        if (resizeObserver) {
          resizeObserver.disconnect();
          resizeObserver = null;
        }

        cancelMouseDrag();
        track.classList.remove('is-scrollable');
        track.style.removeProperty('--tfx-banner-footer-item-width');
        dotsRoot.innerHTML = '';
        dotsRoot.hidden = true;
      }
    });
  }

  function destroySection(section) {
    if (!section) return;
    var instance = instances.get(section);
    if (!instance) return;
    instance.destroy();
    instances.delete(section);
  }

  function initAll(root) {
    var scope = root || document;

    if (scope instanceof Element && scope.matches('.section-tfx-banner-footer')) {
      initSection(scope);
    }

    if (scope.querySelectorAll) {
      var sections = scope.querySelectorAll('.section-tfx-banner-footer');
      for (var i = 0; i < sections.length; i += 1) {
        initSection(sections[i]);
      }
    }
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroySection(findSection(event.target));
  });

  document.addEventListener('shopify:section:select', function (event) {
    initSection(findSection(event.target));
  });

  document.addEventListener('shopify:block:select', function (event) {
    var section = findSection(event.target);
    if (!section) return;

    initSection(section);

    var item = event.target.closest('.tfx-banner-footer__item');
    var instance = getInstance(section);
    if (instance && typeof instance.scrollToItem === 'function' && item) {
      instance.scrollToItem(item);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  window.TFXBannerFooterPager = {
    initAll: initAll,
    destroySection: destroySection
  };
})();
